'use client';

import Image from "next/image";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from 'next-intl';
import { montserratRegular, montserratExtraBold, montserratSemi, montserratMedium } from "../fonts";
import XBackground from './x-background';

type PublicStats = {
  volume24h: number;
  cumulative: number;
  users: number;
};

function formatCompactUsd(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const units = [
    { threshold: 1e12, suffix: 'T' },
    { threshold: 1e9, suffix: 'B' },
    { threshold: 1e6, suffix: 'M' },
    { threshold: 1e3, suffix: 'K' },
  ];
  for (const unit of units) {
    if (abs >= unit.threshold) {
      return `${sign}$${(abs / unit.threshold).toFixed(2)}${unit.suffix}`;
    }
  }
  return `${sign}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatExactUsd(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatUsers(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export default function HeroBanner() {
  const t = useTranslations('home.hero');
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [isScrolledPast, setIsScrolledPast] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [isTradingPlatformVisible, setIsTradingPlatformVisible] = useState(false);
  const lastScrollYRef = useRef(0);
  const currentTranslateYRef = useRef(0);
  const goToDownload = () => window.open('/download', '_self');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public-stats')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('stats'))))
      .then((data: PublicStats) => {
        if (cancelled) return;
        if (
          typeof data?.volume24h !== 'number' ||
          typeof data?.cumulative !== 'number' ||
          typeof data?.users !== 'number'
        ) {
          return;
        }
        setStats(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  
  const handleLaunchApp = () => {
    window.open('https://app.xox.exchange/', '_self');
  };

  // Check if TradingPlatformSection is visible and handle scroll transform
  useEffect(() => {
    const tradingPlatformSection = document.getElementById('trading-platform');
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        // Batch all reads together to avoid forced reflow
        const viewportHeight = window.innerHeight;
        const scrollY = window.scrollY;
        const scrollDirection = scrollY > lastScrollYRef.current ? 'down' : 'up';
        
        // Read all geometry properties in one batch
        let rect: DOMRect | null = null;
        if (tradingPlatformSection) {
          rect = tradingPlatformSection.getBoundingClientRect();
        }
        
        // Hide banner when scrolled past 90% of viewport height
        setIsScrolledPast(scrollY > viewportHeight * 0.9);

        // Check if TradingPlatformSection is on screen
        if (rect) {
          const isVisible = rect.top < viewportHeight && rect.bottom > 0;
          setIsTradingPlatformVisible(isVisible);

          if (isVisible) {
            // When TradingPlatformSection is visible
            const scrollDelta = Math.abs(scrollY - lastScrollYRef.current);
            const speed = Math.min(scrollDelta, 5); // Limit speed for smoothness
            
            if (scrollDirection === 'down') {
              // Scrolling down: increase translateY up to 50% (slower)
              currentTranslateYRef.current = Math.min(50, currentTranslateYRef.current + speed * 0.05);
            } else {
              // Scrolling up: decrease translateY back to 0% (slower)
              currentTranslateYRef.current = Math.max(0, currentTranslateYRef.current - speed * 0.3);
            }
            
            // Batch state updates
            setTranslateY(currentTranslateYRef.current);
          } else if (rect.top >= viewportHeight) {
            // Section hasn't entered viewport yet, reset to 0%
            currentTranslateYRef.current = 0;
            setTranslateY(0);
          }
        }

        lastScrollYRef.current = scrollY;
        rafId = null;
      });
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <section
      id="home-banner"
      className="fixed top-0 left-0 w-full min-h-[100vh] sm:min-h-[90vh] md:min-h-screen px-6 sm:px-12 mb-[120px] md:-mb-[5vh] md:py-24 flex items-center justify-center overflow-hidden"
      style={{ 
        zIndex: 10, 
        willChange: 'transform',
        opacity: isScrolledPast ? 0 : 1,
        pointerEvents: isScrolledPast ? 'none' : 'auto',
        transition: 'opacity 0.3s ease-out',
        transform: `translate(0%, -${translateY}%)`
      }}
    >
      <div className="absolute inset-0 z-10">
        <div id="x-background" style={{ width: '100%', height: '100%', position: 'relative' }}>
          <XBackground />
        </div>
      </div>
      <div className="relative z-10 mx-0 md:mx-[8%] flex max-w-6xl flex-col items-center justify-center text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm mb-8"
        >
          <span className="flex h-2 w-2 rounded-full bg-[color:var(--main-color)]">
            <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-[color:var(--main-color)] opacity-75"></span>
          </span>
          <span className={`${montserratMedium.className} text-xs tracking-wider text-white/70 uppercase`}>{t('badge')}</span>
        </motion.div>
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className={`${montserratRegular.className} text-sm sm:text-base xl:text-[24px] md:pl-[4px] uppercase tracking-wider text-[color:var(--text-primary)]`}
        >
          {t('tagline')}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className={`${montserratExtraBold.className} pb-8 md:pb-16 text-5xl sm:text-6xl md:text-7xl xl:text-9xl text-transparent bg-clip-text bg-gradient-to-b from-white via-white/90 to-white/40 tracking-tighter leading-tight mix-blend-overlay`}
        >
          {t('title')}
          <br />
          <motion.span 
            animate={{ 
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              ease: "linear" 
            }}
            className="px-1 text-transparent bg-clip-text bg-gradient-to-r from-[color:var(--main-color)] via-white to-[color:var(--main-color)] bg-[length:200%_auto] tracking-tighter leading-tight mix-blend-overlay"
          >
            {t('titleHighlight')}
          </motion.span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 pointer-events-auto"
        >
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`${montserratSemi.className} group relative w-44 h-14 bg-white text-black rounded-full text-lg overflow-hidden shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] transition-shadow duration-300 cursor-pointer`}
            onClick={handleLaunchApp}
          >
            <span className="relative z-10 flex items-center justify-center gap-2 transition-transform group-hover:-translate-y-[150%] duration-500">
              {t('startTrading')}
            </span>
            <span className="absolute inset-0 z-10 flex items-center justify-center gap-2 translate-y-[150%] group-hover:translate-y-0 transition-transform duration-500 text-[#3B82F6]">
              {t('launchApp')}
            </span>
          </motion.button>
          
          <motion.button 
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
            whileTap={{ scale: 0.95 }}
            className={`${montserratSemi.className} w-44 h-14 rounded-full border border-white/10 text-white text-lg backdrop-blur-md relative overflow-hidden group cursor-pointer`}
            onClick={goToDownload}
          >
            <span className="relative z-10">{t('download')}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </motion.button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-row gap-4 sm:gap-8 pointer-events-auto pt-8 md:pt-16 items-center justify-center"
        >
          <div className="flex flex-col gap-2">
            <div className={`${montserratMedium.className} text-xs sm:text-sm text-white/70`}>{t('volume24h')}</div>
            <div
              className={`${montserratExtraBold.className} text-lg sm:text-xl text-white whitespace-nowrap`}
              title={stats ? formatExactUsd(stats.volume24h) : undefined}
            >
              {stats ? formatCompactUsd(stats.volume24h) : '—'}
            </div>
          </div>
          <div className="w-0.5 h-[15px] bg-[color:var(--field-border)]"></div>
          <div className="flex flex-col gap-2">
            <div className={`${montserratMedium.className} text-xs sm:text-sm text-white/70`}>{t('cumulative')}</div>
            <div
              className={`${montserratExtraBold.className} text-lg sm:text-xl text-white whitespace-nowrap`}
              title={stats ? formatExactUsd(stats.cumulative) : undefined}
            >
              {stats ? formatCompactUsd(stats.cumulative) : '—'}
            </div>
          </div>
          <div className="w-0.5 h-[15px] bg-[color:var(--field-border)]"></div>
          <div className="flex flex-col gap-2">
            <div className={`${montserratMedium.className} text-xs sm:text-sm text-white/70`}>{t('addresses')}</div>
            <div className={`${montserratExtraBold.className} text-lg sm:text-xl text-white whitespace-nowrap`}>
              {stats ? formatUsers(stats.users) : '—'}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

